import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  UnorderedList,
  ListItem,
  Divider,
  Icon,
  Alert,
  AlertIcon,
  Badge,
  SimpleGrid
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt,
  FaDatabase,
  FaUserShield,
  FaEnvelope,
  FaLock,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Inject the font styles
if (typeof document !== 'undefined' && !document.head.querySelector('[data-privacy-fonts]')) {
  const styleElement = document.createElement('style');
  styleElement.textContent = fontImport;
  styleElement.setAttribute('data-privacy-fonts', 'true');
  document.head.appendChild(styleElement);
}

const MotionBox = motion(Box);

const PrivacyPolicyPage = () => {
  const sections = [
    {
      icon: FaDatabase,
      title: 'What We Collect',
      color: 'blue.500',
      items: [
        'Full name, email address, phone number',
        'Billing and shipping address',
        'Device and browser info (via cookies)',
        'Transaction data (processed securely through Razorpay)'
      ],
      note: 'We do not store your payment information.'
    },
    {
      icon: FaUserShield,
      title: 'How We Use Your Data',
      color: 'green.500',
      items: [
        'To process and ship your orders',
        'To send order updates and customer support messages',
        'To improve your experience via analytics and feedback',
        'To personalize future offers and content for regular updates about our products and New arrivals.'
      ]
    },
    {
      icon: FaLock,
      title: 'Data Security',
      color: 'purple.500',
      content: [
        'Your data is stored securely on encrypted servers.',
        'We do not share or sell your data to third-party marketers.'
      ],
      shareWith: [
        'Our payment processor (Razorpay)',
        'Our delivery partners (courier companies)', 
        'Internal tools used for analytics and communication'
      ]
    },
    {
      icon: FaEnvelope,
      title: 'Marketing & Opt-Out',
      color: 'orange.500',
      content: [
        'We may send you product updates, offers, or event announcements via email or WhatsApp.'
      ],
      optOut: [
        'Clicking "unsubscribe" in our emails',
        'Messaging us on WhatsApp with "STOP"'
      ]
    }
  ];

  const highlights = [
    {
      icon: FaShieldAlt,
      title: 'Secure Storage',
      description: 'Encrypted servers protect your data'
    },
    {
      icon: FaCheckCircle,
      title: 'No Third-Party Sales',
      description: 'We never sell your data to marketers'
    },
    {
      icon: FaUserShield,
      title: 'Easy Opt-Out',
      description: 'Unsubscribe anytime, hassle-free and seamlessly'
    }
  ];

  return (
    <Box 
      minH="100vh" 
      bgImage={`url(${process.env.REACT_APP_BG_URL})`}
      py={{ base: 10, lg: 20 }}
    >
      <Container maxW="container.xl">
        {/* Header Section */}
        <MotionBox
          textAlign="center"
          mb={{ base: 12, lg: 16 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <HStack justify="center" mb={6}>
            <Icon 
              as={FaShieldAlt} 
              boxSize={{ base: 8, lg: 10 }} 
              color="blue.500" 
            />
          </HStack>
          <Heading
            as="h1"
            size={{ base: "2xl", lg: "3xl" }}
            fontFamily="'Playfair Display', serif"
            fontWeight="700"
            color="gray.800"
            mb={6}
            lineHeight="1.2"
          >
            Privacy Policy
          </Heading>
          <Text
            fontSize={{ base: "lg", lg: "xl" }}
            color="gray.600"
            fontFamily="'Inter', sans-serif"
            maxW="700px"
            mx="auto"
            lineHeight="1.7"
            mb={8}
          >
            Your privacy is important to us. This policy explains how we collect, use, 
            and protect your personal information when you use our services.
          </Text>

          {/* Quick Highlights */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} maxW="800px" mx="auto">
            {highlights.map((highlight, index) => (
              <MotionBox
                key={highlight.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <VStack
                  spacing={3}
                  p={6}
                  bg="white"
                  borderRadius="xl"
                  boxShadow="0 4px 15px rgba(0, 0, 0, 0.08)"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Icon as={highlight.icon} boxSize={6} color="blue.500" />
                  <Text
                    fontFamily="'Inter', sans-serif"
                    fontWeight="600"
                    color="gray.800"
                    fontSize="sm"
                    textAlign="center"
                  >
                    {highlight.title}
                  </Text>
                  <Text
                    fontSize="xs"
                    color="gray.600"
                    fontFamily="'Inter', sans-serif"
                    textAlign="center"
                    lineHeight="1.4"
                  >
                    {highlight.description}
                  </Text>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </MotionBox>

        {/* Main Content Sections */}
        <VStack spacing={{ base: 8, lg: 12 }}>
          {sections.map((section, index) => (
            <MotionBox
              key={section.title}
              w="full"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <Box
                bg="white"
                borderRadius="2xl"
                p={{ base: 6, lg: 10 }}
                boxShadow="0 10px 30px rgba(0, 0, 0, 0.08)"
                border="1px solid"
                borderColor="gray.200"
                position="relative"
                overflow="hidden"
              >
                {/* Section Header */}
                <HStack spacing={4} mb={6} align="center">
                  <Box
                    p={3}
                    borderRadius="xl"
                    bg={`${section.color.split('.')[0]}.50`}
                    border="1px solid"
                    borderColor={`${section.color.split('.')[0]}.200`}
                  >
                    <Icon as={section.icon} boxSize={6} color={section.color} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading
                      as="h2"
                      size={{ base: "lg", lg: "xl" }}
                      fontFamily="'Playfair Display', serif"
                      fontWeight="600"
                      color="gray.800"
                    >
                      {section.title}
                    </Heading>
                  </VStack>
                </HStack>

                <Divider mb={6} borderColor="gray.200" />

                {/* Section Content */}
                <VStack spacing={4} align="start">
                  {/* Items List */}
                  {section.items && (
                    <Box w="full">
                      <UnorderedList spacing={3} pl={4}>
                        {section.items.map((item, itemIndex) => (
                          <ListItem key={itemIndex}>
                            <Text
                              fontSize={{ base: "md", lg: "lg" }}
                              fontFamily="'Inter', sans-serif"
                              color="gray.700"
                              lineHeight="1.6"
                            >
                              {item}
                            </Text>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </Box>
                  )}

                  {/* Content Text */}
                  {section.content && section.content.map((text, textIndex) => (
                    <Text
                      key={textIndex}
                      fontSize={{ base: "md", lg: "lg" }}
                      fontFamily="'Inter', sans-serif"
                      color="gray.700"
                      lineHeight="1.7"
                      w="full"
                    >
                      {text}
                    </Text>
                  ))}

                  {/* Special Note */}
                  {section.note && (
                    <Alert status="info" borderRadius="lg" bg="blue.50" border="1px solid" borderColor="blue.200">
                      <AlertIcon color="blue.500" />
                      <Text
                        fontSize="md"
                        fontFamily="'Inter', sans-serif"
                        color="blue.800"
                        fontWeight="500"
                      >
                        {section.note}
                      </Text>
                    </Alert>
                  )}

                  {/* Share With Section */}
                  {section.shareWith && (
                    <Box w="full">
                      <Text
                        fontSize={{ base: "md", lg: "lg" }}
                        fontFamily="'Inter', sans-serif"
                        color="gray.700"
                        lineHeight="1.7"
                        mb={3}
                      >
                        We may share relevant details only with:
                      </Text>
                      <UnorderedList spacing={2} pl={4}>
                        {section.shareWith.map((partner, partnerIndex) => (
                          <ListItem key={partnerIndex}>
                            <Text
                              fontSize="md"
                              fontFamily="'Inter', sans-serif"
                              color="gray.700"
                              lineHeight="1.6"
                            >
                              {partner}
                            </Text>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </Box>
                  )}

                  {/* Opt-Out Section */}
                  {section.optOut && (
                    <Box w="full">
                      <Text
                        fontSize={{ base: "md", lg: "lg" }}
                        fontFamily="'Inter', sans-serif"
                        color="gray.700"
                        lineHeight="1.7"
                        mb={3}
                      >
                        You can opt out anytime by:
                      </Text>
                      <VStack spacing={3} align="start">
                        {section.optOut.map((method, methodIndex) => (
                          <HStack key={methodIndex} spacing={3}>
                            <Box
                              w={2}
                              h={2}
                              borderRadius="full"
                              bg="orange.400"
                              flexShrink={0}
                              mt={2}
                            />
                            <Text
                              fontSize="md"
                              fontFamily="'Inter', sans-serif"
                              color="gray.700"
                              lineHeight="1.6"
                            >
                              {method}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </Box>
            </MotionBox>
          ))}
        </VStack>

        {/* Contact Section */}
        <MotionBox
          mt={{ base: 12, lg: 16 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Alert 
            status="warning" 
            borderRadius="2xl" 
            p={{ base: 6, lg: 8 }}
            bg="orange.50" 
            border="2px solid" 
            borderColor="orange.200"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
          >
            <AlertIcon boxSize={8} color="orange.500" mb={4} />
            <VStack spacing={4}>
              <Heading
                as="h3"
                size="lg"
                fontFamily="'Playfair Display', serif"
                color="orange.800"
              >
                Questions About Your Privacy?
              </Heading>
              <Text
                fontSize={{ base: "md", lg: "lg" }}
                fontFamily="'Inter', sans-serif"
                color="orange.700"
                lineHeight="1.6"
                maxW="600px"
              >
                If you have any questions about this Privacy Policy or how we handle your data, 
                please don't hesitate to contact us.
              </Text>
              <VStack spacing={2}>
                <HStack spacing={4} flexWrap="wrap" justify="center">
                  <Badge colorScheme="orange" fontSize="md" px={3} py={1} borderRadius="full">
                    Email: agnir.info@gmail.com
                  </Badge>
                  <Badge colorScheme="orange" fontSize="md" px={3} py={1} borderRadius="full">
                    WhatsApp: +91 73876 57127
                  </Badge>
                </HStack>
              </VStack>
            </VStack>
          </Alert>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;